import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { resolveWorkflowRunServiceBaseUrl } from '../portal/workflowbuilder/workflowRunStartService';

type NodeRunOutputRow = {
  nodeRunId?: string;
  nodeId?: string;
  iterationIndex?: number | null;
  outputJson?: string;
};

type HtmlSlice = {
  key: string;
  nodeId: string;
  nodeRunId: string;
  html: string;
};

function tryParseJson(value: unknown): unknown {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function stripMarkdownHtmlFence(text: string): string {
  let t = text.trim();
  t = t.replace(/^```(?:html)?\s*/i, '');
  t = t.replace(/\s*```\s*$/i, '');
  return t.trim();
}

function assistantJsonToHtml(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const html = stripMarkdownHtmlFence(raw);
  return html || null;
}

function extractAssistantHtmlFromOutputJson(outputJson: string): string | null {
  const outer = tryParseJson(outputJson) as Record<string, unknown> | null;
  if (!outer) return null;

  const prevRaw = outer.previousOutput;
  let inner: Record<string, unknown> | null = null;
  if (typeof prevRaw === 'string') {
    inner = tryParseJson(prevRaw) as Record<string, unknown> | null;
  } else if (prevRaw && typeof prevRaw === 'object') {
    inner = prevRaw as Record<string, unknown>;
  }

  if (inner) {
    const payload = inner.payload;
    if (payload && typeof payload === 'object') {
      const aj = (payload as Record<string, unknown>).assistantJson;
      const html = assistantJsonToHtml(aj);
      if (html) return html;
    }
  }

  const topPayload = outer.payload;
  if (topPayload && typeof topPayload === 'object') {
    const aj = (topPayload as Record<string, unknown>).assistantJson;
    return assistantJsonToHtml(aj);
  }

  return null;
}

function collectHtmlSlices(rows: NodeRunOutputRow[]): HtmlSlice[] {
  const out: HtmlSlice[] = [];
  for (const row of rows) {
    const oj = row.outputJson;
    if (typeof oj !== 'string' || !oj.trim()) continue;
    const outer = tryParseJson(oj) as Record<string, unknown> | null;
    const kind = outer?.kind;
    if (kind !== 'html-viewer') continue;
    const html = extractAssistantHtmlFromOutputJson(oj);
    if (!html) continue;
    const nodeId = (row.nodeId ?? 'node').trim() || 'node';
    const nodeRunId = (row.nodeRunId ?? '').trim() || String(out.length);
    out.push({
      key: `${nodeRunId}:${nodeId}`,
      nodeId,
      nodeRunId,
      html,
    });
  }
  return out;
}

function isNodeRunOutputArray(data: unknown): data is NodeRunOutputRow[] {
  return Array.isArray(data);
}

/** Mirrors apiClient request headers (Bearer + tenant) for the run-logs service. */
function buildRunLogsAuthHeaders(): HeadersInit {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = localStorage.getItem('token');
  if (token) headers.Authorization = `Bearer ${token}`;
  const tenantId = localStorage.getItem('tenant_id');
  if (tenantId) headers['X-Tenant-Id'] = tenantId;
  return headers;
}

const WorkflowRunHtmlViewerPage: React.FC = () => {
  const { workflowId = '', runId = '' } = useParams<{ workflowId: string; runId: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slices, setSlices] = useState<HtmlSlice[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSlices([]);
    setSelectedKey(null);

    const base = resolveWorkflowRunServiceBaseUrl();
    if (!base) {
      setError(
        'Missing REACT_APP_API_URL_workflow_run_logs. Add it to your environment, then restart the dev server.'
      );
      setLoading(false);
      return;
    }

    const wid = workflowId.trim();
    const rid = runId.trim();
    if (!wid || !rid) {
      setError('workflowId and runId are required in the URL.');
      setLoading(false);
      return;
    }

    const url = `${base}/workflows/${encodeURIComponent(wid)}/run-logs/${encodeURIComponent(
      rid
    )}/node-run-outputs`;

    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: buildRunLogsAuthHeaders(),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        setError(t.trim() || `Request failed (HTTP ${res.status}).`);
        return;
      }
      const data: unknown = await res.json();
      if (!isNodeRunOutputArray(data)) {
        setError('Unexpected response: expected a JSON array.');
        return;
      }
      const next = collectHtmlSlices(data);
      setSlices(next);
      if (next.length > 0) {
        setSelectedKey(next[next.length - 1]!.key);
      } else {
        setError('No html-viewer output with assistantJson was found for this run.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load node run outputs.');
    } finally {
      setLoading(false);
    }
  }, [workflowId, runId]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = useMemo(
    () => slices.find((s) => s.key === selectedKey) ?? slices[slices.length - 1] ?? null,
    [slices, selectedKey]
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-200">
        <p className="text-sm">Loading…</p>
      </div>
    );
  }

  if (error && slices.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 px-6 text-center text-slate-200">
        <p className="max-w-lg text-sm text-rose-300">{error}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded-md border border-slate-600 bg-slate-900 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-800"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-950">
      {slices.length > 1 ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-slate-800 px-3 py-2">
          <label htmlFor="wf-html-node" className="text-xs text-slate-400">
            Output
          </label>
          <select
            id="wf-html-node"
            value={active?.key ?? ''}
            onChange={(e) => setSelectedKey(e.target.value)}
            className="max-w-[min(100vw-6rem,42rem)] rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100"
          >
            {slices.map((s) => (
              <option key={s.key} value={s.key}>
                {s.nodeId}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {active ? (
        <iframe
          title="Workflow HTML output"
          className="min-h-0 w-full flex-1 border-0 bg-white"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          srcDoc={active.html}
        />
      ) : null}
    </div>
  );
};

export default WorkflowRunHtmlViewerPage;
